// React core and hooks
import React, { useEffect, useRef, useState } from "react"

// Third-party libraries
import { Modal, notification } from "antd"
import { Autocomplete } from "@react-google-maps/api"
import { useDispatch, useSelector } from "react-redux"

// Project utilities and constants
import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import { setLoader } from "store/actions"
import { sanitizeText } from "utils/utils"
import { deliveryZonesToPolygons } from "utils/geojson"

// Project components
import { GoogleMaps, Icon } from "components"
import { getOrders, OrderFilters } from "../../utils"

// Styles
import "./styles.scss"

// ------------------------------------------------------------------
// Shared address-validation API call. Exported so the Orders screen's
// ApprovalModal (which finalizes the same flow for related orders) can
// reuse the exact same request shape.
// ------------------------------------------------------------------
export async function callAddressValidationAPI(data: {
	orderId: string
	originalAddress: string
	formattedAddress: string
	addressNotes?: string
	coordinates: {
		lat: number | string
		lng: number | string
	}
	postalCode: string
	city: string
	country: string
	lot: string
	relatedOrders?: string[]
	confirmIncompleteAddress?: boolean
}) {
	return await API.post({
		url: Endpoints.uriOrders("addressValidation"),
		data,
	})
}

// Latest address-validation source ("system" | "user" | null), read from the
// order's addressValidationHistory. The most recent entry reflects the current
// state: "user" (validated in the modal) or "system" (auto-matched on import).
const getValidationSource = (order: any): "system" | "user" | null => {
	const history = order?.addressValidationHistory
	if (Array.isArray(history) && history.length > 0) {
		return history[history.length - 1]?.source || null
	}
	return null
}

// Heuristic: does the address contain a house/street number? Country-agnostic —
// the known postal code is passed in and removed (token-based) so its digits
// don't count, then we look for a standalone number token (e.g. "5", "12A").
// Mirrors the backend hasHouseNumber used to gate saving into validatedAddresses.
const addressHasNumber = (address?: string, postalCode?: string): boolean => {
	if (!address || typeof address !== "string") return false
	// Diacritics are irrelevant for finding a numeric token, so we only need to
	// lowercase, drop punctuation to spaces and collapse whitespace.
	const normalize = (s: string) =>
		s
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, " ")
			.replace(/\s+/g, " ")
			.trim()
	const tokens = normalize(address).split(" ").filter(Boolean)
	const postalTokens = normalize(postalCode || "")
		.split(" ")
		.filter(Boolean)
	for (const pt of postalTokens) {
		const i = tokens.indexOf(pt)
		if (i !== -1) tokens.splice(i, 1)
	}
	return tokens.some((token) => /^\d{1,5}[a-z]?$/.test(token))
}

// Minimum distance (in meters) the map-clicked location must differ from the
// order's original location to allow saving when the address text is unchanged.
const MIN_LOCATION_CHANGE_METERS = 20

// Haversine distance between two lat/lng points, in meters.
const getDistanceInMeters = (
	a?: { lat?: number | null; lng?: number | null },
	b?: { lat?: number | null; lng?: number | null }
): number => {
	if (
		!a ||
		!b ||
		typeof a.lat !== "number" ||
		typeof a.lng !== "number" ||
		typeof b.lat !== "number" ||
		typeof b.lng !== "number"
	) {
		return 0
	}

	const toRad = (value: number) => (value * Math.PI) / 180
	const earthRadius = 6371000 // meters
	const dLat = toRad(b.lat - a.lat)
	const dLng = toRad(b.lng - a.lng)
	const lat1 = toRad(a.lat)
	const lat2 = toRad(b.lat)

	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2

	return 2 * earthRadius * Math.asin(Math.sqrt(h))
}

const translateOrderWarningAndError = (key: string) => {
	switch (key) {
		case "date":
			return Strings.orders.deliveryDate || key
		case "slot":
			return Strings.orders.slot || key
		case "postCode":
			return Strings.fields.zipCode || key
		case "address":
			return Strings.orders.address || key
		case "coordinates":
			return Strings.orders.coordinates || key
		case "phone":
			return Strings.orders.phoneNumber || key
		default:
			return key
	}
}

// The value bag submitted to the backend / handed to the ApprovalModal. Carries
// both the raw `originalAddress` (used to decide the incomplete-address prompt)
// and the editable `formattedAddress` (the address actually validated/stored).
export interface AddressFormValues {
	originalAddress: string
	formattedAddress: string
	addressNotes: string
	lat: number | string
	lng: number | string
	postalCode: string
	city: string
	country: string
	lot: string
}

interface AddressValidationModalProps {
	orders: any[]
	showAddressValidationModal: string | undefined
	setShowAddressValidationModal: (orderId?: any) => void
	business: any
	contract: any
	selectedStatus: string[]
	date: any
	pagination: any
	setPagination: any
	search: string | undefined | null
	attributeFilters: OrderFilters
	setDefaultLots: (lots: any[]) => void
	setShowApprovalModal: (show: boolean) => void
	setApprovalData: (data: any) => void
	deliveryZones?: any[]
}

const AddressValidationModal: React.FC<AddressValidationModalProps> = ({
	orders,
	showAddressValidationModal,
	setShowAddressValidationModal,
	business,
	contract,
	selectedStatus,
	date,
	pagination,
	setPagination,
	search,
	attributeFilters,
	setDefaultLots,
	setShowApprovalModal,
	setApprovalData,
	deliveryZones,
}) => {
	const dispatch = useDispatch()

	const mobile = useSelector((state: any) => state.mobile)
	const loading = useSelector((state: any) => state.loading)

	const order = orders?.find(
		(entry: any) => entry.orderId === showAddressValidationModal
	)

	// Controlled field state. The raw "Morada original" is read-only reference
	// text; "Morada formatada" (editableAddress) is the field being validated.
	const [originalAddress, setOriginalAddress] = useState<string>("")
	const [editableAddress, setEditableAddress] = useState<string>("")
	const [addressNotes, setAddressNotes] = useState<string>("")
	const [lot, setLot] = useState<string>("")
	// Geocoded fields — set by autocomplete/map, cleared on a manual edit so the
	// backend re-runs Google address validation on the typed text.
	const [coordinates, setCoordinates] = useState<{
		lat?: number
		lng?: number
	}>({})
	const [postalCode, setPostalCode] = useState<string>("")
	const [city, setCity] = useState<string>("")
	const [country, setCountry] = useState<string>("")
	// True once the user has interacted with any field (gates the save button).
	const [touched, setTouched] = useState<boolean>(false)
	// Holds the pending form values while the incomplete-address confirmation
	// modal is open (null when it's closed).
	const [incompleteValues, setIncompleteValues] =
		useState<AddressFormValues | null>(null)
	const [orderBoundingBox, setOrderBoundingBox] = useState<any>(null)

	const lastValidCenter = useRef<{ lat: number; lng: number } | undefined>(
		undefined
	)
	const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
	const addressInputRef = useRef<HTMLInputElement | null>(null)

	// Seed the fields whenever the modal opens for an order.
	useEffect(() => {
		if (!showAddressValidationModal || !order) return

		setOriginalAddress(order.orderAddress?.address || "")
		setEditableAddress(
			order.formattedAddress?.address
				? sanitizeText(order.formattedAddress.address)
				: ""
		)
		setAddressNotes(order.addressNotes || "")
		setLot(order.lot || "")
		setCoordinates(order.formattedAddress?.location || {})
		setPostalCode(order.formattedAddress?.postCode || "")
		setCity(order.formattedAddress?.city || "")
		setCountry(order.formattedAddress?.country || "")
		setTouched(false)

		if (order.googleAddressValidation?.google_geocode?.bounds) {
			const { low, high } = order.googleAddressValidation.google_geocode.bounds
			// Construct polygon points clockwise
			setOrderBoundingBox([
				{ lat: low.latitude, lng: low.longitude }, // SW
				{ lat: low.latitude, lng: high.longitude }, // SE
				{ lat: high.latitude, lng: high.longitude }, // NE
				{ lat: high.latitude, lng: low.longitude }, // NW
				{ lat: low.latitude, lng: low.longitude }, // Close polygon (back to SW)
			])
		} else {
			setOrderBoundingBox(null)
		}
	}, [showAddressValidationModal, order])

	// Warnings / errors derived from the order.
	let orderWarningsList: string[] = []
	let orderErrorsList: string[] = []
	if (order?.orderWarnings) {
		orderWarningsList = Object.entries(order.orderWarnings)
			.map(([key, value]) => (value ? key : ""))
			.filter(Boolean)
	}
	if (order?.orderErrors) {
		orderErrorsList = Object.entries(order.orderErrors)
			.map(([key, value]) => (value ? key : ""))
			.filter(Boolean)
	}
	const hasWarnings = orderWarningsList.length > 0
	const hasErrors = orderErrorsList.length > 0

	const validationSource = getValidationSource(order)

	const handleCancel = () => {
		setShowAddressValidationModal()
		setEditableAddress("")
		setOriginalAddress("")
		setAddressNotes("")
		setLot("")
		setCoordinates({})
		setPostalCode("")
		setCity("")
		setCountry("")
		setTouched(false)
		setOrderBoundingBox(null)
	}

	// Applies a manually-typed / pasted formatted address: keep the text, clear
	// the geocoded fields so the backend re-validates it with Google.
	const applyManualAddress = (value: string) => {
		setEditableAddress(value)
		setCoordinates({})
		setPostalCode("")
		setCity("")
		setCountry("")
		setTouched(true)
	}

	// Applies a resolved place/location: fill the formatted + geocoded fields.
	const applyResolvedAddress = (resolved: {
		address?: string
		lat?: number
		lng?: number
		postalCode?: string
		city?: string
		country?: string
	}) => {
		const sanitized = sanitizeText(resolved.address || "")
		setEditableAddress(sanitized)
		setPostalCode(resolved.postalCode || "")
		setCity(resolved.city || "")
		setCountry(resolved.country || "")
		setCoordinates(
			resolved.lat != null && resolved.lng != null
				? { lat: resolved.lat, lng: resolved.lng }
				: {}
		)
		setTouched(true)
	}

	const handlePlaceChanged = () => {
		const place = autocompleteRef.current?.getPlace()
		if (!place || !place.geometry) return

		const postal = place.address_components?.find((component: any) =>
			component.types.includes("postal_code")
		)?.long_name
		const localityCity = place.address_components?.find(
			(component: any) =>
				component.types.includes("locality") ||
				component.types.includes("administrative_area_level_2")
		)?.long_name
		const placeCountry = place.address_components?.find((component: any) =>
			component.types.includes("country")
		)?.long_name

		if (place.formatted_address) {
			applyResolvedAddress({
				address: place.formatted_address,
				lat: place.geometry.location?.lat(),
				lng: place.geometry.location?.lng(),
				postalCode: postal,
				city: localityCity,
				country: placeCountry,
			})
		}
	}

	const buildValues = (): AddressFormValues => ({
		originalAddress,
		formattedAddress: editableAddress,
		addressNotes,
		lat: coordinates?.lat ?? "",
		lng: coordinates?.lng ?? "",
		postalCode,
		city,
		country,
		lot,
	})

	// Submits the address validation. `confirmIncompleteAddress` tells the backend
	// it may persist an address that is missing a street/door number to the shared
	// validatedAddresses store (only set when the user explicitly confirmed).
	const submitAddressValidation = async (
		values: AddressFormValues,
		confirmIncompleteAddress: boolean
	) => {
		dispatch(setLoader(true))

		try {
			const response = await callAddressValidationAPI({
				orderId: showAddressValidationModal as string,
				// The editable "Morada Formatada" is the address to validate/store.
				// The order's raw orderAddress is preserved server-side and is not
				// sent from here. When coordinates are present (autocomplete/map) the
				// backend uses this directly; when cleared (manual edit) it re-runs
				// Google address validation on it.
				originalAddress: values.formattedAddress,
				formattedAddress: values.formattedAddress,
				addressNotes: values.addressNotes,
				coordinates: {
					lat: values.lat,
					lng: values.lng,
				},
				postalCode: values.postalCode,
				city: values.city,
				country: values.country,
				lot: values.lot,
				confirmIncompleteAddress,
			})

			if (response.ok) {
				// Check if approval is required for related orders
				if (response.data?.results?.requiresApproval) {
					setApprovalData({
						originalOrderId: response.data.results.originalOrderId,
						relatedOrders: response.data.results.relatedOrders,
						message: response.data.results.message,
						formValues: values, // Store form values to use later
					})
					setShowApprovalModal(true)
					handleCancel() // Close the address validation modal
				} else {
					notification.success({
						message: Strings.orders.addressConfirmation,
						description:
							response?.data?.message || Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
					handleCancel()

					// Refresh orders
					getOrders(
						dispatch,
						selectedStatus,
						search,
						business,
						contract,
						date,
						null, // Selected lot, currently only used in aggregation tab
						pagination,
						setPagination,
						setDefaultLots,
						attributeFilters
					)
				}
			} else {
				notification.error({
					message: Strings.orders.addressValidationError,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (error) {
			notification.error({
				message: Strings.orders.addressValidationError,
				description: "An error occurred while validating address.",
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			dispatch(setLoader(false))
		}
	}

	const handleSave = () => {
		const values = buildValues()
		// The address auto-saves to the shared validatedAddresses store on
		// validate. The stored matchKey is built from the RAW original address
		// (the read-only "Morada Original" field), so THAT is what decides whether
		// a future different-house import could collide — check it for a house/door
		// number, not the edited formatted value.
		if (
			!addressHasNumber(values.originalAddress, order?.orderAddress?.postCode)
		) {
			// Open the confirmation modal; the user chooses validate-only or
			// validate-and-save, or closes (X) to take no action.
			setIncompleteValues(values)
			return
		}
		submitAddressValidation(values, false)
	}

	// No coordinates yet (manual edit) → backend must run Google address
	// validation; otherwise we can save directly.
	const hasCoordinates =
		isFinite(coordinates?.lat as number) && isFinite(coordinates?.lng as number)

	const saveDisabled =
		loading ||
		editableAddress === "" ||
		!touched ||
		(editableAddress === order?.formattedAddress?.address &&
			getDistanceInMeters(coordinates, order?.formattedAddress?.location) <
				MIN_LOCATION_CHANGE_METERS)

	const mapCenter = (() => {
		if (
			isFinite(coordinates?.lat as number) &&
			isFinite(coordinates?.lng as number)
		) {
			lastValidCenter.current = {
				lat: coordinates.lat as number,
				lng: coordinates.lng as number,
			}
		}
		return lastValidCenter.current
	})()

	return (
		<>
			<Modal
				centered
				closable={false}
				onCancel={handleCancel}
				visible={!!showAddressValidationModal}
				footer={null}
				bodyStyle={{ padding: 0 }}
				className="AVModalRoot"
				width={mobile ? "100%" : 760}
			>
				<div className="AVModal">
					{/* Header: title + order tag + validation-source badge + close */}
					<div className="AVModal__header">
						<div className="AVModal__title">
							<h2>{Strings.orders.addressConfirmation}</h2>
							<span className="tag">{showAddressValidationModal}</span>
							{validationSource && (
								<span
									className={`badge ${
										validationSource === "system"
											? "badge--info"
											: "badge--success"
									}`}
								>
									{validationSource === "system"
										? Strings.orders.validatedBySystem
										: Strings.orders.validatedByUser}
								</span>
							)}
						</div>
						<button
							type="button"
							className="btn btn--ghost btn--icon"
							aria-label={Strings.generic.cancel}
							onClick={handleCancel}
						>
							<Icon name="close" />
						</button>
					</div>

					{/* Scrollable body */}
					<div className="AVModal__body">
						{/* Raw imported address — read-only reference text */}
						<div className="AVModal__field">
							<span className="eyebrow">{Strings.orders.originalAddress}</span>
							<p className="body" style={{ color: "var(--ink-secondary)" }}>
								{originalAddress || "—"}
							</p>
						</div>

						<div className="route-divider">
							<span className="route-divider__line" />
						</div>

						{/* Formatted address — the editable field (Places autocomplete) */}
						<div className="AVModal__field">
							<label className="label" htmlFor="av-formatted-address">
								{Strings.orders.formattedAddress} <span className="req">*</span>
							</label>
							<Autocomplete
								className="AVModal__autocomplete"
								onLoad={(autocomplete) =>
									(autocompleteRef.current = autocomplete)
								}
								onPlaceChanged={handlePlaceChanged}
							>
								<input
									id="av-formatted-address"
									ref={addressInputRef}
									className="input"
									type="text"
									value={editableAddress}
									placeholder={Strings.orders.formattedAddress}
									onChange={(e) =>
										applyManualAddress(sanitizeText(e.target.value))
									}
									onPaste={(e) => {
										e.preventDefault()
										const pasted = e.clipboardData.getData("text")
										// Fix pasted text from encoding corruption (e.g. UTF-8 bytes).
										const sanitized = sanitizeText(pasted)

										// Splice at the current cursor selection, preserving text
										// already in the field.
										const input = e.currentTarget
										const start = input.selectionStart ?? editableAddress.length
										const end = input.selectionEnd ?? editableAddress.length
										const newValue =
											editableAddress.slice(0, start) +
											sanitized +
											editableAddress.slice(end)

										applyManualAddress(newValue)

										// Defer a native "input" event until after React flushes the
										// state to the DOM. Google Autocomplete watches for this
										// event on the native input to trigger place suggestions —
										// without it, suggestions never appear.
										setTimeout(() => {
											addressInputRef.current?.dispatchEvent(
												new Event("input", { bubbles: true })
											)
										}, 0)
									}}
								/>
							</Autocomplete>
						</div>

						{/* Notes + Lot */}
						<div className="AVModal__grid AVModal__grid--2">
							<div className="AVModal__field">
								<label className="label" htmlFor="av-notes">
									{Strings.orders.notes}
								</label>
								<input
									id="av-notes"
									className="input"
									type="text"
									value={addressNotes}
									onChange={(e) => {
										setAddressNotes(e.target.value)
										setTouched(true)
									}}
								/>
							</div>
							<div className="AVModal__field">
								<label className="label" htmlFor="av-lot">
									Lot
								</label>
								<input
									id="av-lot"
									className="input"
									type="text"
									value={lot}
									onChange={(e) => {
										setLot(e.target.value)
										setTouched(true)
									}}
								/>
							</div>
						</div>

						{/* Geocoded (read-only) fields */}
						<div className="AVModal__grid AVModal__grid--3">
							<div className="AVModal__field">
								<label className="label" htmlFor="av-lat">
									Latitude
								</label>
								<input
									id="av-lat"
									className="input num input--readonly"
									type="text"
									value={coordinates?.lat ?? ""}
									readOnly
								/>
							</div>
							<div className="AVModal__field">
								<label className="label" htmlFor="av-lng">
									Longitude
								</label>
								<input
									id="av-lng"
									className="input num input--readonly"
									type="text"
									value={coordinates?.lng ?? ""}
									readOnly
								/>
							</div>
							<div className="AVModal__field">
								<label className="label" htmlFor="av-postal">
									Postal Code
								</label>
								<input
									id="av-postal"
									className="input num input--readonly"
									type="text"
									value={postalCode}
									readOnly
								/>
							</div>
						</div>

						<div className="AVModal__grid AVModal__grid--2">
							<div className="AVModal__field">
								<label className="label" htmlFor="av-city">
									City
								</label>
								<input
									id="av-city"
									className="input input--readonly"
									type="text"
									value={city}
									readOnly
								/>
							</div>
							<div className="AVModal__field">
								<label className="label" htmlFor="av-country">
									Country
								</label>
								<input
									id="av-country"
									className="input input--readonly"
									type="text"
									value={country}
									readOnly
								/>
							</div>
						</div>

						{/* Warnings / errors cards */}
						<div className="AVModal__grid AVModal__grid--2">
							<div
								className={`status-card ${
									hasWarnings ? "status-card--warning" : "status-card--ok"
								}`}
							>
								<div className="status-card__row">
									<Icon name={hasWarnings ? "warning" : "correct-symbol"} />
									{hasWarnings ? (
										<ul>
											{orderWarningsList.map((warning) => (
												<li key={warning}>
													{translateOrderWarningAndError(warning)}
												</li>
											))}
										</ul>
									) : (
										<span>{Strings.orders.noWarning}s!</span>
									)}
								</div>
							</div>
							<div
								className={`status-card ${
									hasErrors ? "status-card--danger" : "status-card--ok"
								}`}
							>
								<div className="status-card__row">
									<Icon name={hasErrors ? "warning" : "correct-symbol"} />
									{hasErrors ? (
										<ul>
											{orderErrorsList.map((error) => (
												<li key={error}>
													{translateOrderWarningAndError(error)}
												</li>
											))}
										</ul>
									) : (
										<span>{Strings.orders.noError}s!</span>
									)}
								</div>
							</div>
						</div>

						{/* Interactive map — autocomplete/click-to-set, delivery-zone
						    polygons and the order bounding box */}
						<div className="AVModal__field">
							<span className="eyebrow">Localização no mapa</span>
							<div className="AVModal__map">
								<GoogleMaps
									loadScript={false}
									center={mapCenter}
									zoom={15}
									mapContainerStyle={{
										width: "100%",
										height: "100%",
									}}
									options={{ panControl: false }}
									markers={
										hasCoordinates
											? [
													{
														lat: coordinates.lat as number,
														lng: coordinates.lng as number,
													},
												]
											: []
									}
									polygons={[
										...deliveryZonesToPolygons(deliveryZones),
										...(orderBoundingBox ? [{ paths: orderBoundingBox }] : []),
									]}
									onClick={{
										address: true,
										function: (location: {
											address?: string
											lat: number
											lng: number
											postalCode?: string
											city?: string
											country?: string
										}) => {
											// Map click resolves a full address — fill the formatted
											// (editable) field and the geocoded fields. The raw
											// orderAddress is left untouched.
											applyResolvedAddress(location)
										},
									}}
								/>
							</div>
						</div>
					</div>

					{/* Footer actions */}
					<div className="AVModal__footer">
						<button
							type="button"
							className="btn btn--secondary"
							onClick={handleCancel}
							disabled={loading}
						>
							{Strings.generic.cancel}
						</button>
						<button
							type="button"
							className="btn btn--primary"
							onClick={handleSave}
							disabled={saveDisabled}
						>
							{hasCoordinates
								? Strings.generic.save
								: Strings.orders.validateAddress}
						</button>
					</div>
				</div>
			</Modal>

			{/* Incomplete-address confirmation. Closing (X) takes no action; the two
			    buttons validate the order either without saving to the shared store,
			    or validating and saving. */}
			<Modal
				centered
				closable
				onCancel={() => setIncompleteValues(null)}
				visible={!!incompleteValues}
				footer={null}
				bodyStyle={{ padding: 0 }}
				className="AVModalRoot"
				width={mobile ? "100%" : 480}
			>
				<div className="AVModal">
					<div className="AVModal__header">
						<div className="AVModal__title">
							<h2>{Strings.orders.incompleteAddressTitle}</h2>
						</div>
					</div>
					<div className="AVConfirm__body">
						<p>{Strings.orders.incompleteAddressWarning}</p>
					</div>
					<div className="AVModal__footer">
						<button
							type="button"
							className="btn btn--secondary"
							disabled={loading}
							onClick={() => {
								const pending = incompleteValues
								setIncompleteValues(null)
								if (pending) submitAddressValidation(pending, true)
							}}
						>
							{Strings.orders.validateAndSave}
						</button>
						<button
							type="button"
							className="btn btn--primary"
							disabled={loading}
							onClick={() => {
								const pending = incompleteValues
								setIncompleteValues(null)
								if (pending) submitAddressValidation(pending, false)
							}}
						>
							{Strings.orders.validateWithoutSaving}
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}

export default AddressValidationModal
